'use client';
import { type Coach, isDateBefore } from '@leland-dev/leland-ui-library';
import {
  type GetStaticPaths,
  type GetStaticPathsResult,
  type GetStaticProps,
  type GetStaticPropsResult,
} from 'next';
import { useSearchParams } from 'next/navigation';
import React, { type FC, useCallback } from 'react';

import { type GoalName } from '../../../../__generated-gql-types__/globalTypes';
import {
  type CoachesTabsDataProps,
  getStaticCoachesTabsProps,
} from '../../../../components/category/CoachesTabs';
import Page from '../../../../components/page/Page';
import PageContent, {
  getStaticPageContentQuery,
  type PageContentDataProps,
} from '../../../../components/page/PageContent';
import { SRP_PAGE_SIZE } from '../../../../components/search/CoachesSectionWithFilters';
import {
  getStaticOfferingPackagesProps,
  type OfferingPackagesData,
} from '../../../../components/search/OfferingPackages';
import SRP from '../../../../components/search/SRP';
import {
  CategoryLevelPageCategoryBySlugDocument,
  type CategoryLevelPageCategoryBySlugQuery,
  type CategoryLevelPageCategoryBySlugQueryVariables,
  type CategoryLevelPageCategoryFragment,
  CategoryLevelPageCoachesBySlugsDocument,
  type CategoryLevelPageCoachesBySlugsQuery,
  type CategoryLevelPageCoachesBySlugsQueryVariables,
  CategoryLevelPageGetActiveCategoriesDocument,
  type CategoryLevelPageGetActiveCategoriesQuery,
  type CategoryLevelPageGetActiveCategoriesQueryVariables,
  type CategoryLevelPageLelandPlusContributorCoachBatchResponseFragment,
  CategoryLevelPageLelandPlusContributorsDocument,
  type CategoryLevelPageLelandPlusContributorsQuery,
  type CategoryLevelPageLelandPlusContributorsQueryVariables,
  CategoryLevelPageSearchBootcampsDocument,
  type CategoryLevelPageSearchBootcampsQuery,
  type CategoryLevelPageSearchBootcampsQueryVariables,
  CategoryLevelPageSearchCoachesDocument,
  type CategoryLevelPageSearchCoachesQuery,
  type CategoryLevelPageSearchCoachesQueryVariables,
  CategoryLevelPageSearchCoachesTotalDocument,
  type CategoryLevelPageSearchCoachesTotalQuery,
  type CategoryLevelPageSearchCoachesTotalQueryVariables,
  type CategoryLevelPageSkuFragment,
  CategoryLevelPageStaticLexDocument,
  type CategoryLevelPageStaticLexQuery,
  type CategoryLevelPageStaticLexQueryVariables,
  type CategoryLevelPageSubCategoryFragment,
} from '../../../../components/search/__generated-gql-types__/[[...subCategoryAndPage]].generated';
import {
  getStaticUpcomingClassesCarouselProps,
  type UpcomingClassesCarouselDataProps,
} from '../../../../components/search/carousels/UpcomingClassesCarousel';
import { LexSlug } from '../../../../context/LexContext';
import { initializeApollo } from '../../../../lib/apolloClient';
import { ServerTimeLogger } from '../../../../utils/analytics/server-log-time';
import { CATEGORY_REMAP } from '../../../../utils/category';
import { getCoachUrl } from '../../../../utils/coach';
import {
  __STAGING__,
  APPLICANT_SITE_URL,
  MASTERS_PROGRAMS_URN,
} from '../../../../utils/constants';
import { COHORT_BANNER_DATA_BY_CATEGORY_ID_OR_SUBCATEGORY_SLUG } from '../../../../utils/events';
import {
  formatGoalCoachesLabel,
  formatGoalForUrl,
} from '../../../../utils/formatters';
import {
  getSkusForStartingPriceFromFsCache,
  skusForStartingPriceDataFetcher,
} from '../../../../utils/fs-cache/skus-for-starting-price-cache';
import { isLiveLelandPlusCategory } from '../../../../utils/leland-plus';
import { getBatchResponseStart } from '../../../../utils/pagination';
import { generateSearchPath } from '../../../../utils/search';

interface CohortBannerData {
  title: string;
  description: string;
  totalSessions: number;
  startAt: number;
  eventUrl: string;
  coaches: Coach[];
}

interface CategoryLevelPageProps {
  goal: GoalName;
  page: number;
  category: CategoryLevelPageCategoryFragment;
  subCategory: Nullable<CategoryLevelPageSubCategoryFragment>;
  coaches: Nullable<CategoryLevelPageSearchCoachesQuery>;
  offeringPackagesProps: OfferingPackagesData;
  upcomingClassesCarouselProps: UpcomingClassesCarouselDataProps;
  coachesTabProps: CoachesTabsDataProps;
  pageContentProps: PageContentDataProps;
  lelandPlusContributors: Nullable<CategoryLevelPageLelandPlusContributorCoachBatchResponseFragment>;
  bootcampInfo: Nullable<CohortBannerData>;
  skusData: CategoryLevelPageSkuFragment[];
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type CategoryLevelPagePathParams = {
  goal: string;
  category: string;
  subCategoryAndPage?: string[];
};
/*
  TODO: the way pagination is tied to page component takes a bit more unwinding. We'll do this step when we get to creating app layout files.
*/

const CategoryLevelPage: FC<CategoryLevelPageProps> = ({
  goal,
  page,
  category,
  subCategory,
  coaches: initialCoaches,
  offeringPackagesProps,
  upcomingClassesCarouselProps,
  coachesTabProps,
  pageContentProps,
  lelandPlusContributors,
  bootcampInfo,
  skusData,
}) => {
  const searchParams = useSearchParams();
  const goalTitle = `${formatGoalCoachesLabel(category.goal)}`;
  const categoryTitle = category ? `${category.name} ` : '';
  const subCategoryTitle = subCategory
    ? `${subCategory.name}${category.id == MASTERS_PROGRAMS_URN ? " Master's" : ''} `
    : '';
  const defaultPageTitle = `${subCategoryTitle || categoryTitle}${goalTitle}`;
  const pageTitle = category.srpTitle || defaultPageTitle;

  const coaches = initialCoaches;
  const totalCoachResults = coaches?.searchCoaches.total ?? 0;

  const buildPaginationUrlString = useCallback(
    (page: Possible<number>) => {
      const category = searchParams.get('category');
      const subCategoryAndPage = searchParams.get('subCategoryAndPage') ?? [];
      const { subCategory } = getSubCategoryAndPage(
        (subCategoryAndPage as string[]) || [],
      );
      return generateSearchPath({
        goal: goal,
        categorySlug: category,
        subCategorySlug: subCategory,
        page: (page ?? 1).toString(),
      });
    },
    [searchParams, goal],
  );

  return (
    <Page
      title={pageTitle}
      canonicalLink={`${APPLICANT_SITE_URL}${generateSearchPath({
        goal: category.goal,
        categorySlug: category.slug,
        subCategorySlug: subCategory?.slug,
      })}`}
      prev={page > 1 ? buildPaginationUrlString(page - 1) : undefined}
      next={
        page * SRP_PAGE_SIZE < totalCoachResults
          ? buildPaginationUrlString(page + 1)
          : undefined
      }
    >
      <PageContent initialData={pageContentProps}>
        <SRP
          goal={goal}
          page={page}
          category={category}
          subCategory={subCategory}
          coaches={coaches}
          offeringPackagesProps={offeringPackagesProps}
          upcomingClassesCarouselProps={upcomingClassesCarouselProps}
          coachesTabProps={coachesTabProps}
          lelandPlusContributors={lelandPlusContributors}
          bootcampInfo={bootcampInfo}
          skusData={skusData}
        />
      </PageContent>
    </Page>
  );
};

export const getStaticPaths: GetStaticPaths<
  CategoryLevelPagePathParams
> = async () => {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query<
    Possible<CategoryLevelPageGetActiveCategoriesQuery>,
    CategoryLevelPageGetActiveCategoriesQueryVariables
  >({
    query: CategoryLevelPageGetActiveCategoriesDocument,
    // get up to 1000 categories
    variables: { count: 1000 },
  });
  const results: GetStaticPathsResult<CategoryLevelPagePathParams> = {
    paths: [],
    fallback: 'blocking',
  };
  if (data?.searchCategories) {
    for (const {
      id: categoryUrn,
      slug,
      goal,
      subCategoryList,
      shouldShowSubCategories,
    } of data.searchCategories.results) {
      const paramBase = {
        goal: formatGoalForUrl(goal),
        category: slug,
      };
      const { data: coachesTotal } = await apolloClient.query<
        Possible<CategoryLevelPageSearchCoachesTotalQuery>,
        CategoryLevelPageSearchCoachesTotalQueryVariables
      >({
        query: CategoryLevelPageSearchCoachesTotalDocument,
        variables: { categoryUrn },
      });
      results.paths.push({
        params: {
          ...paramBase,
          subCategoryAndPage: [],
        },
      });
      for (
        let i = 1;
        i < Math.ceil((coachesTotal?.searchCoaches.total ?? 0) / SRP_PAGE_SIZE);
        i++
      ) {
        results.paths.push({
          params: {
            ...paramBase,
            subCategoryAndPage: [(i + 1).toString()],
          },
        });
      }
      if (subCategoryList && shouldShowSubCategories) {
        const subCategoryCoaches = await Promise.all(
          subCategoryList.map(async (subCategory) => {
            const { data: subCategoryCoachesTotal } = await apolloClient.query<
              Possible<CategoryLevelPageSearchCoachesTotalQuery>,
              CategoryLevelPageSearchCoachesTotalQueryVariables
            >({
              query: CategoryLevelPageSearchCoachesTotalDocument,
              variables: {
                categoryUrn,
                subCategoryListFilter: [subCategory.id],
              },
            });
            return {
              subCategory,
              coachesTotal: subCategoryCoachesTotal?.searchCoaches.total ?? 0,
            };
          }),
        );
        subCategoryCoaches.forEach(({ subCategory, coachesTotal }) => {
          results.paths.push({
            params: {
              ...paramBase,
              subCategoryAndPage: [subCategory.slug],
            },
          });
          for (let i = 1; i < Math.ceil(coachesTotal / SRP_PAGE_SIZE); i++) {
            results.paths.push({
              params: {
                ...paramBase,
                subCategoryAndPage: [subCategory.slug, (i + 1).toString()],
              },
            });
          }
        });
      }
    }
  }

  return results;
};

const NOT_FOUND_PROPS: GetStaticPropsResult<CategoryLevelPageProps> = {
  notFound: true,
};

interface SubCategoryAndPage {
  subCategory: Nullable<string>;
  page: number;
}

const getSubCategoryAndPage = (
  subCategoryAndPageArray: string[],
): SubCategoryAndPage => {
  if (subCategoryAndPageArray.length === 0) {
    return {
      subCategory: null,
      page: 1,
    };
  }
  if (subCategoryAndPageArray.length === 2) {
    return {
      subCategory: subCategoryAndPageArray[0],
      page: parseInt(subCategoryAndPageArray[1], 10),
    };
  }
  if (subCategoryAndPageArray.length === 1) {
    const parsedValue = parseInt(subCategoryAndPageArray[0], 10);
    if (isNaN(parsedValue)) {
      return {
        subCategory: subCategoryAndPageArray[0],
        page: 1,
      };
    }
    return {
      subCategory: null,
      page: parsedValue,
    };
  }
  return {
    subCategory: null,
    page: 1,
  };
};

export const getStaticProps: GetStaticProps<
  CategoryLevelPageProps,
  CategoryLevelPagePathParams
> = async ({ params }) => {
  const { goal, category, subCategoryAndPage } = params ?? {};
  if (!goal || !category) {
    return NOT_FOUND_PROPS;
  }

  const TimeLoggerClient = new ServerTimeLogger();

  const { subCategory, page } = getSubCategoryAndPage(subCategoryAndPage ?? []);

  if (category && category in CATEGORY_REMAP) {
    return {
      redirect: {
        destination: generateSearchPath({
          goal: goal as GoalName,
          categorySlug: CATEGORY_REMAP[category],
          subCategorySlug: subCategory,
          page: page.toString(),
        }),
        permanent: true,
      },
    };
  }

  console.group('Building SRP', goal, category, subCategory, page);
  const startTime = Date.now();

  const apolloClient = initializeApollo();
  const { data, errors } = await apolloClient.query<
    Possible<CategoryLevelPageCategoryBySlugQuery>,
    CategoryLevelPageCategoryBySlugQueryVariables
  >({
    query: CategoryLevelPageCategoryBySlugDocument,
    variables: { slug: category },
  });
  TimeLoggerClient.logTime('category');

  if (!data?.categoryBySlug) {
    console.log('⛔️ no category');
    console.groupEnd();
    if (!errors || errors?.some((e) => e.extensions?.statusCode === 404)) {
      return NOT_FOUND_PROPS;
    }

    // If not 404, throw to explicitly treat as 5xx
    console.warn(errors);
    throw new Error('Error fetching category');
  }

  // if goal mismatched with category goal, redirect to path with proper goal
  if (formatGoalForUrl(data.categoryBySlug.goal) !== goal) {
    console.log('⛔️ goal mismatch');
    console.groupEnd();
    return {
      redirect: {
        destination: generateSearchPath({
          goal: data.categoryBySlug.goal,
          categorySlug: category,
          subCategorySlug: subCategory,
          page: page.toString(),
        }),
        permanent: true,
      },
    };
  }

  const subCategoryFound = subCategory
    ? data.categoryBySlug.subCategoryList?.find(
        (subcat) => subcat.slug === subCategory,
      )
    : null;

  // if subCategory not found on category, redirect to base category path
  if (subCategory && !subCategoryFound) {
    console.log('⛔️ subcategory not found');
    console.groupEnd();
    return {
      redirect: {
        destination: generateSearchPath({
          goal: goal as GoalName,
          categorySlug: category,
          subCategorySlug: null,
          page: page.toString(),
        }),
        permanent: false,
      },
    };
  }

  const categoryUrn = data.categoryBySlug.id;

  const coachesData = await apolloClient.query<
    Possible<CategoryLevelPageSearchCoachesQuery>,
    CategoryLevelPageSearchCoachesQueryVariables
  >({
    query: CategoryLevelPageSearchCoachesDocument,
    variables: {
      categoryUrn,
      subCategoryListFilter: subCategoryFound?.id,
      offset: getBatchResponseStart(page, SRP_PAGE_SIZE),
      count: SRP_PAGE_SIZE,
    },
  });
  TimeLoggerClient.logTime('coaches');

  const offeringPackagesData = await getStaticOfferingPackagesProps({
    category: data.categoryBySlug,
    subCategory: subCategoryFound,
  });
  TimeLoggerClient.logTime('packages');

  const upcomingClassesCarouselData =
    await getStaticUpcomingClassesCarouselProps({
      categoryUrn,
    });
  TimeLoggerClient.logTime('classes');

  const coachesTabData = await getStaticCoachesTabsProps({
    categoryUrn,
    subCategoryUrn: subCategoryFound?.id,
  });
  TimeLoggerClient.logTime('get started with a coach section coaches');

  const pageContentCategoriesHeaderData = await getStaticPageContentQuery();
  TimeLoggerClient.logTime('page content categories header');

  const lelandPlusContributorsData =
    isLiveLelandPlusCategory(categoryUrn) && page === 1
      ? await apolloClient.query<
          Possible<CategoryLevelPageLelandPlusContributorsQuery>,
          CategoryLevelPageLelandPlusContributorsQueryVariables
        >({
          query: CategoryLevelPageLelandPlusContributorsDocument,
          variables: {
            categoryUrn,
          },
        })
      : null;
  TimeLoggerClient.logTime('leland+ contributors');

  const lexData = await apolloClient.query<
    Possible<CategoryLevelPageStaticLexQuery>,
    CategoryLevelPageStaticLexQueryVariables
  >({
    query: CategoryLevelPageStaticLexDocument,
    variables: {
      slugs: [LexSlug.FEATURE_BOOTCAMPS_ON_STACK],
    },
  });
  TimeLoggerClient.logTime('lex');

  let bootcampInfo: Possible<CohortBannerData>;
  if (lexData.data?.lexListBySlugs[0].isRamped) {
    const bootcampData = subCategoryFound?.id
      ? await apolloClient.query<
          Possible<CategoryLevelPageSearchBootcampsQuery>,
          CategoryLevelPageSearchBootcampsQueryVariables
        >({
          query: CategoryLevelPageSearchBootcampsDocument,
          variables: {
            categoriesFilter: [categoryUrn],
            subCategoriesFilter: subCategoryFound?.id,
            startDate: new Date().getTime(),
          },
        })
      : await apolloClient.query<
          Possible<CategoryLevelPageSearchBootcampsQuery>,
          CategoryLevelPageSearchBootcampsQueryVariables
        >({
          query: CategoryLevelPageSearchBootcampsDocument,
          variables: {
            categoriesFilter: [categoryUrn],
            startDate: new Date().getTime(),
          },
        });
    TimeLoggerClient.logTime('bootcamps');

    const bootcampToDisplay = bootcampData.data?.searchBootcamps.results.find(
      (bootcamp) => bootcamp.cohorts.length > 0,
    );

    bootcampInfo = bootcampToDisplay
      ? {
          title: bootcampToDisplay.title,
          startAt: bootcampToDisplay.cohorts[0].startAt,
          description: bootcampToDisplay.description,
          totalSessions: bootcampToDisplay.cohorts[0].totalSessions,
          eventUrl: bootcampToDisplay.landingPageUrl,
          coaches: bootcampToDisplay.cohorts[0].coaches.map((coach) => ({
            id: coach.id,
            firstName: coach.user.firstName,
            lastName: coach.user.lastName,
            slug: coach.slug,
            pictureLink: coach.user.pictureLink ?? undefined,
            url: getCoachUrl({
              coachSlug: coach.slug,
              categorySlug: category,
            }),
          })),
        }
      : null;
  } else {
    const cohortBannerData = ((subCategory
      ? COHORT_BANNER_DATA_BY_CATEGORY_ID_OR_SUBCATEGORY_SLUG[subCategory]
      : null) ??
      COHORT_BANNER_DATA_BY_CATEGORY_ID_OR_SUBCATEGORY_SLUG[
        categoryUrn
      ]) as Optional<
      (typeof COHORT_BANNER_DATA_BY_CATEGORY_ID_OR_SUBCATEGORY_SLUG)[string]
    >;

    const coachSlugsToFetchForCohortBanner =
      cohortBannerData && isDateBefore(Date.now(), cohortBannerData.date)
        ? cohortBannerData.coachSlugs
        : null;

    const cohortBannerCoachesData = coachSlugsToFetchForCohortBanner?.length
      ? await apolloClient.query<
          CategoryLevelPageCoachesBySlugsQuery,
          CategoryLevelPageCoachesBySlugsQueryVariables
        >({
          query: CategoryLevelPageCoachesBySlugsDocument,
          variables: {
            slugs: coachSlugsToFetchForCohortBanner,
          },
        })
      : null;
    TimeLoggerClient.logTime('cohort banner coaches');

    bootcampInfo =
      cohortBannerData && isDateBefore(Date.now(), cohortBannerData.date)
        ? {
            title: cohortBannerData.title,
            startAt: cohortBannerData.date.getTime(),
            description: cohortBannerData.description,
            totalSessions: cohortBannerData.numSessions,
            coaches: (cohortBannerCoachesData?.data?.coachesBySlugs ?? []).map(
              (coach) => ({
                id: coach.id,
                firstName: coach.user.firstName,
                lastName: coach.user.lastName,
                slug: coach.slug,
                pictureLink: coach.user.pictureLink ?? undefined,
                url: getCoachUrl({
                  coachSlug: coach.slug,
                  categorySlug: category,
                }),
              }),
            ),
            eventUrl: cohortBannerData?.url,
          }
        : null;
  }

  let skusData: CategoryLevelPageProps['skusData'];
  try {
    skusData = await getSkusForStartingPriceFromFsCache();
  } catch {
    skusData = await skusForStartingPriceDataFetcher();
  }
  TimeLoggerClient.logTime('skus');

  console.log(
    `Overall time: ${Date.now() - startTime}ms,`,
    goal,
    category,
    subCategory,
    page,
  );
  console.groupEnd();

  return {
    props: {
      goal: data.categoryBySlug.goal,
      page,
      category: data.categoryBySlug,
      subCategory: subCategoryFound ?? null,
      coaches: coachesData.data ?? null,
      offeringPackagesProps: offeringPackagesData,
      upcomingClassesCarouselProps: upcomingClassesCarouselData,
      coachesTabProps: coachesTabData,
      pageContentProps: pageContentCategoriesHeaderData,
      lelandPlusContributors:
        lelandPlusContributorsData?.data?.lelandPlusContributors ?? null,
      skusData: skusData ?? [],
      bootcampInfo: bootcampInfo ?? null,
    },
    revalidate: 3600,
  };
};

export default CategoryLevelPage;
